/**
 * First-run tour (about 60 seconds, 6 steps): testnet basics -> connect -> test FRAME faucet (24h cooldown per
 * wallet) -> factions -> timer / last buyer -> THALIA Drones -> buy first key. Dismissal is stored in localStorage.
 */
import { useState, useCallback, useEffect } from 'react';
import { useFaucet } from './FaucetButton';
import { createPortal } from 'react-dom';
import { formatFRAME } from '../utils/format';
import { CHAIN } from '../config/chain';

const STORAGE_KEY = 'nt3d_how_it_works_dismissed_v2';

export function useHowItWorks() {
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== '1'; } catch { return true; }
  });
  const dismiss = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setOpen(false);
  }, []);
  const reopen = useCallback(() => setOpen(true), []);
  return { open, dismiss, reopen };
}

function fmtTime(s: number) {
  if (s < 0) return '--:--:--';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return [h, m, sec].map(n => String(n).padStart(2, '0')).join(':');
}

interface Props {
  open: boolean;
  onClose: () => void;
  timeRemaining: number;
  pot: bigint;
  account: string | null;
  isDemoSite: boolean;
  onBuyFirstKey: () => void;
}

export default function HowItWorksOverlay({ open, onClose, timeRemaining, pot, account, isDemoSite, onBuyFirstKey }: Props) {
  const [step, setStep] = useState(0);
  const f = useFaucet(open ? account : null);
  const [gotFrame, setGotFrame] = useState(false);
  useEffect(() => {
    const h = () => setGotFrame(true);
    window.addEventListener('nt3d:faucet-success', h);
    return () => window.removeEventListener('nt3d:faucet-success', h);
  }, []);
  useEffect(() => { if (open) setStep(0); }, [open]);
  if (!open) return null;
  const testnet = isDemoSite || CHAIN.isTestnet;
  const faucetLabel = !account ? 'Connect a wallet first (step 2)'
    : !f.onSepolia ? `Switch your wallet to ${CHAIN.chainName || 'Base Sepolia'}`
    : gotFrame ? '✓ Test FRAME on its way'
    : f.busy ? 'Sending…'
    : f.check.status === 'cooldown' ? `Already claimed: next claim in about ${f.check.hoursLeft ?? 24}h`
    : 'Get free test FRAME';
  const steps: { title: string; body: JSX.Element }[] = [
    { title: 'Welcome to NT3D', body: <>
      <p>A last-buyer-wins game on a testnet. Everything here uses <b>test FRAME</b>: free demo tokens with no value.</p>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="rounded-xl bg-black/50 border border-neon-magenta/40 p-3 text-center">
          <div className="text-xs uppercase tracking-widest text-gray-400">Timer</div>
          <div className="font-mono text-2xl text-neon-magenta">{fmtTime(timeRemaining)}</div>
        </div>
        <div className="rounded-xl bg-black/50 border border-yellow-400/40 p-3 text-center">
          <div className="text-xs uppercase tracking-widest text-gray-400">Pot</div>
          <div className="font-mono text-2xl text-yellow-300">{formatFRAME(pot)}</div>
          <div className="text-xs text-gray-500">test FRAME</div>
        </div>
      </div></> },
    { title: 'Connect a wallet', body: <>
      <p>MetaMask or any browser wallet works. We add the Base Sepolia test network for you.</p>
      <button type="button" onClick={() => window.dispatchEvent(new Event('nt3d:open-wallet'))} disabled={!!account}
        className="mt-3 w-full py-3 rounded-xl bg-neon-cyan/20 border border-neon-cyan text-neon-cyan disabled:opacity-60 font-orbitron text-sm uppercase tracking-wider" data-testid="tour-connect">
        {account ? '✓ Wallet connected' : 'Connect wallet'}
      </button></> },
    { title: 'Grab test FRAME', body: <>
      <p>Claim free test FRAME from the faucet, once per wallet every 24 hours. You also need a little Base Sepolia test ETH for gas.</p>
      {testnet && (
        <button type="button" onClick={() => void f.request()} disabled={!account || !f.onSepolia || f.busy || gotFrame || f.check.status === 'cooldown'}
          className="mt-3 w-full py-3 rounded-xl bg-purple-600/80 hover:bg-purple-500 disabled:opacity-60 font-orbitron text-sm uppercase tracking-wider" data-testid="tour-faucet">
          {faucetLabel}
        </button>
      )}</> },
    { title: 'Pick a faction', body: <p>Before your first key you join one of 8 factions (Raven, Ape, Moth, Viper, Rabbit, Wolf, Fox, Owl). Your faction is locked for the round, and faction standings count toward the season.</p> },
    { title: 'Last buyer takes the pot', body: <p>Each key adds 30 seconds to the timer (capped at 24 hours). Every key also gets a share of later key buys, in test FRAME. When the timer hits zero, whoever bought last takes the pot.</p> },
    { title: 'Humans vs THALIA Drones', body: <p>Quiet rounds may get <b>🤖 THALIA Drones</b>: house wallets with personalities that keep the clock moving. They can never win: if a drone holds the last key, the last human buyer wins. They back off as more humans join. (Coming soon: off for now.)</p> },
  ];
  const last = step === steps.length - 1;
  const s = steps[step];
  return createPortal(
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="hiw-title" data-testid="first-run-tour">
      <div className="relative w-full max-w-lg rounded-2xl border border-neon-cyan/40 bg-void-black p-6 shadow-2xl shadow-neon-cyan/20">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 text-gray-400 hover:text-white text-xl leading-none">&times;</button>
        <div className="text-xs uppercase tracking-widest text-gray-500">Step {step + 1} of {steps.length}</div>
        <h2 id="hiw-title" className="font-orbitron text-2xl text-neon-cyan mb-3">{s.title}</h2>
        <div className="text-sm text-gray-300 leading-relaxed min-h-[120px]">{s.body}</div>
        <div className="tour-dots" aria-hidden="true">{steps.map((_, i) => <i key={i} className={i === step ? 'on' : ''} />)}</div>
        <div className="flex gap-2">
          {step > 0 && <button type="button" onClick={() => setStep(step - 1)} className="flex-1 py-3 rounded-xl border border-gray-600 text-gray-300 font-orbitron text-sm uppercase">Back</button>}
          {!last && <button type="button" onClick={() => setStep(step + 1)} className="flex-[2] py-3 rounded-xl bg-neon-cyan/20 border border-neon-cyan text-neon-cyan font-orbitron text-sm uppercase tracking-wider" data-testid="tour-next">Next</button>}
          {last && <button type="button" onClick={() => { onBuyFirstKey(); onClose(); }} className="flex-[2] py-3 rounded-xl bg-neon-cyan/20 border border-neon-cyan text-neon-cyan font-orbitron text-sm uppercase tracking-wider" data-testid="tour-buy">
            {isDemoSite ? 'Buy my first demo key' : 'Buy my first key'}
          </button>}
        </div>
        <button onClick={onClose} className="mt-2 w-full py-2 text-sm text-gray-500 hover:text-white">Skip the tour</button>
      </div>
    </div>,
    document.body,
  );
}
